import React from "react";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Mission from "../../../interfaces/robot/missions/mission";
import { useSharedSelector } from "../../../store/SharedProvider";
import { NavigatorAvailability, NavigatorName, NavigatorType } from "../../../enums/command-hub/navigator";
import { NavigatorChangeProps } from "../Selectors/NavigatorSelection";
import NavigatorSelection from "../Selectors/NavigatorSelection";
import ZoneSelection from "../Selectors/ZoneSelection";
import { AutoFillInNavigator } from "../../../enums/command-hub/navigator";
import { navigators } from "../../../interfaces/robot/navigator";
import { selectIsAdvancedModeEnabled } from "../../../store/app-configurations/app-configurations-selectors";
import { TabPanelA11yProps } from "../../General/TabPanel";

enum CleaningType {
    ZoneCleaning,
    RoomCleaning,
    MaintenanceCleaning,
}

const MissionNavigator: React.FC<{
    mission: Partial<Mission>;
    onMissionChange: (newMission: Partial<Mission>) => void;
    roomId?: string;
    children?: React.ReactNode;
    includePadding?: boolean;
}> = ({ mission, roomId, onMissionChange, children, includePadding }) => {
    const { t } = useTranslation();

    const matchesSmUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

    const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));

    const filteredAvailableNavigators = [
        ...navigators?.filter(
            (n) => n.availability === NavigatorAvailability.Public && (roomId == null || n.type === NavigatorType.CleaningNavigator)
        ),
    ];

    if (isAdvancedModeEnabled) {
        filteredAvailableNavigators.push(
            ...[
                ...navigators?.filter(
                    (n) => n.availability === NavigatorAvailability.AdvancedModeOnly && (roomId == null || n.type === NavigatorType.CleaningNavigator)
                ),
            ]
        );
    }

    if (mission.mode != null && !filteredAvailableNavigators.some(n => n.name === mission.mode)) {
        const selectedNavigator = navigators.find(n => n.name === mission.mode);
        if (selectedNavigator != null) {
            filteredAvailableNavigators.push(selectedNavigator)
        }
    }

    const handleNavigatorSelectChange = (payload: Partial<NavigatorChangeProps>) => {
        onMissionChange({
            ...mission,
            mode: payload.navigatorName ?? mission.mode,
            doWallFollow: payload.doWallFollow ?? mission.doWallFollow,
            repeat: payload.repeats ?? mission.repeat,
            selectedZoneIds: payload.navigatorName != null ? [] : mission.selectedZoneIds,
            selectedLandmarkIds: payload.navigatorName != null ? [] : mission.selectedLandmarkIds,
            fixedOrder: payload.navigatorName != null ? false : mission.fixedOrder,
        });
    };

    const zoneSelectionChangeHandler = (selectedZoneIds: string[], fixedOrder: boolean) => {
      onMissionChange({
        ...mission,
        selectedZoneIds,
        fixedOrder,
      });
    };

    const landmarkSelectionChangeHandler = (landmarkId: string) => {
        onMissionChange({
            ...mission,
            selectedLandmarkIds: [landmarkId],
        });
    };

    const switchModeChangeHandler = (_event: React.ChangeEvent<{}>, newValue: number) => {
        handleNavigatorSelectChange({
            navigatorName:
                newValue === CleaningType.ZoneCleaning
                    ? NavigatorName.ZoneCleaning
                    : newValue === CleaningType.MaintenanceCleaning
                        ? NavigatorName.Maintenance
                        : filteredAvailableNavigators.length > 0
                            ? filteredAvailableNavigators[0].name
                            : ""
        });
    };

    const isZoneMapMode = mission.mode === NavigatorName.ZoneCleaning && roomId != null && roomId !== "";
    const isMaintenanceMapMode = mission.mode === NavigatorName.Maintenance && roomId != null && roomId !== "";
    const showCardsInLeftColumn = !isZoneMapMode && !isMaintenanceMapMode;

    return (
      <React.Fragment>
        {/* Tabs row — always fixed height, sits outside the keyed content box */}
        {roomId != null && (
          <Grid item xs={12} px={includePadding ? 2 : undefined}>
            <Tabs
              variant="fullWidth"
              id="schedule-entry-navigator-select"
              value={
                mission.mode === NavigatorName.ZoneCleaning
                  ? CleaningType.ZoneCleaning
                  : mission.mode === NavigatorName.Maintenance
                    ? CleaningType.MaintenanceCleaning
                    : CleaningType.RoomCleaning
              }
              onChange={switchModeChangeHandler}
            >
              <Tab
                label={t("zone-cleaning")}
                {...TabPanelA11yProps(CleaningType.ZoneCleaning)}
                sx={{ fontSize: matchesSmUp ? "0.87rem" : "small" }}
              />
              <Tab
                label={t("rooms-cleaning")}
                {...TabPanelA11yProps(CleaningType.RoomCleaning)}
                sx={{ fontSize: matchesSmUp ? "0.87rem" : "small" }}
              />
              <Tab
                label={t("maintenance-cleaning")}
                {...TabPanelA11yProps(CleaningType.MaintenanceCleaning)}
                sx={{ fontSize: matchesSmUp ? "0.87rem" : "small" }}
              />
            </Tabs>
          </Grid>
        )}

        {/*
         * key={mission.mode} remounts on every mode switch, re-triggering the
         * CSS fade-in so all content below the Tabs transitions as one smooth unit.
         */}
        <Grid
          container
          key={mission.mode}
          role="tabpanel"
          id={`simple-tabpanel-${mission.mode}`}
          aria-labelledby={`simple-tab-${mission.mode}`}
          sx={{
            "@keyframes kMissionFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
            animation: "kMissionFadeIn 0.2s ease-in",
            pt: 0.5,
          }}
        >
          {/* Left column: map (zone/landmark selection) or mode-picker cards — fixed, never scrolls on md+ */}
          <Grid item xs={12} md={6} sx={{ height: { sx: showCardsInLeftColumn ? undefined : 380, md: 380 } }}>
            {isZoneMapMode && (
              <ZoneSelection
                roomId={roomId}
                onZoneSelectionChanged={zoneSelectionChangeHandler}
                fixedOrder={mission.fixedOrder ?? false}
                selectedZoneIds={mission.selectedZoneIds ?? []}
              />
            )}
            {isMaintenanceMapMode && (
              <ZoneSelection
                roomId={roomId}
                onZoneSelectionChanged={zoneSelectionChangeHandler}
                onLandmarkSelectionChange={landmarkSelectionChangeHandler}
                fixedOrder={mission.fixedOrder ?? false}
                selectedLandmarkId={
                  mission.selectedLandmarkIds != null && mission.selectedLandmarkIds.length > 0
                    ? mission.selectedLandmarkIds[0]
                    : ""
                }
              />
            )}
            {showCardsInLeftColumn && (
              <Box sx={{ pl: { xs: 0, md: 2 } }}>
                <NavigatorSelection
                  variant="cards"
                  navigatorName={mission.mode ?? AutoFillInNavigator}
                  navigators={filteredAvailableNavigators}
                  onChange={handleNavigatorSelectChange}
                />
              </Box>
            )}
          </Grid>

          {/* Right column: remaining config — scrolls independently on md+ */}
          <Grid item xs={12} md={6} sx={{ pl: { xs: includePadding ? 2 : 0, md: 2 }, pr: includePadding ? 2 : 0 }}>
            <Stack spacing={2}>
              <NavigatorSelection
                variant="config"
                navigatorName={mission.mode ?? AutoFillInNavigator}
                navigators={filteredAvailableNavigators}
                onChange={handleNavigatorSelectChange}
                doWallFollow={mission.doWallFollow ?? true}
                repeats={mission.repeat ?? 1}
              />
              {children}
            </Stack>
          </Grid>
        </Grid>
      </React.Fragment>
    );
};

export default MissionNavigator;
